/* eslint-disable max-classes-per-file */
import { toSnakeCase } from './String';
/**
 * Common operations to modify a component state array property
 * This library will safely modify the state without reference conflicts
 */
export function cloneArray<T = unknown>(array: T) {
  // Deep clone
  return JSON.parse(JSON.stringify(array)) as T;
}

export function addElement<T = unknown>(array: T[], element: T) {
  return [...array, element];
}

export function removeElement<T = unknown>(array: T[], index: number) {
  return array.filter((_, ix) => ix !== index);
}

export function updateElement<T = unknown>(
  array: T[],
  index: number,
  update: (element: T) => T
) {
  return array.map((element: T, ix: number) =>
    index === ix ? update(element) : element
  );
}

type SimpleObject = Record<string, string>;

export const keyToSnakeCase = (array: Array<SimpleObject>, key: string) =>
  array.reduce((acc: Array<SimpleObject>, obj: SimpleObject) => {
    acc.push({ ...obj, [key]: toSnakeCase(obj[key]) });
    return acc;
  }, []);

export const ascendingSort = (
  a: string | number,
  b: string | number
): number => {
  const aValue = typeof a === 'string' ? a.toLowerCase() : a;
  const bValue = typeof b === 'string' ? b.toLowerCase() : b;

  if (aValue > bValue) {
    return 1;
  }

  if (bValue > aValue) {
    return -1;
  }

  return 0;
};

export const descendingSort = (
  a: string | number,
  b: string | number
): number => -1 * ascendingSort(a, b);

// Aa-Zz with NULL names at the end
export const descendingNullableSort = (
  a: string | null,
  b: string | null
): number => {
  if (a === b) return 0;
  if (a === null || a === '') return 1;
  if (b === null || b === '') return -1;
  return a.localeCompare(b);
};

export const chunk = <T>(input: T[], size: number) =>
  input.reduce(
    (arr: T[][], item: T, idx: number) =>
      idx % size === 0
        ? [...arr, [item]]
        : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]],
    []
  );

export const uniqBy = <T>(arr: T[], predicate: (item: T) => string) => {
  const cb =
    typeof predicate === 'function' ? predicate : (o: T) => o[predicate];
  const result: T[] = [];
  const map = new Map();

  arr.forEach((item) => {
    const key = item === null || item === undefined ? item : cb(item);

    if (!map.has(key)) {
      map.set(key, item);
      result.push(item);
    }
  });

  return result;
};

export const intersection = <T>(arrays: T[][]) =>
  arrays.reduce((a, b) => a.filter((value) => b.includes(value)));

// Extract top-level ids from an array of objects (like schemas)
export const flatIds = <T extends { id: string }>(arr: T[]) =>
  arr.map((a) => a.id);
